import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const navLinks = [
  { name: 'Home', href: '/#home', isHash: true },
  { name: 'About', href: '/#about', isHash: true },
  { name: 'Services', href: '/services', isHash: false },
  { name: 'Gallery', href: '/#gallery', isHash: true },
  { name: 'Areas', href: '/#areas', isHash: true },
  { name: 'Contact', href: '/#contact', isHash: true },
];

export const Navbar = ({ onOpenQuote }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.isHash) {
      if (location.pathname !== '/') {
        window.location.href = link.href;
      } else {
        const hash = link.href.replace('/', '');
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a 
            href="#home" 
            onClick={(e) => { e.preventDefault(); handleNavClick({ href: '/#home', isHash: true }); }}
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/nctjn6no_ChatGPT%20Image%20Mar%201%2C%202026%2C%2003_19_59%20PM.png" 
              alt="Your Family House Cleaning Logo"
              className="h-40 md:h-48 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.isHash ? (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link); }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-sky-600 
                             transition-colors duration-200 rounded-lg hover:bg-sky-50"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-sky-600 
                             transition-colors duration-200 rounded-lg hover:bg-sky-50"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a 
              href="tel:9132309208" 
              className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
              data-testid="navbar-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">(913) 230-9208</span>
            </a>
            <Button
              onClick={onOpenQuote}
              className="rounded-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 
                         text-white px-5 py-2 shadow-lg shadow-pink-500/30 transition-all duration-300 
                         hover:shadow-xl hover:shadow-pink-500/40"
              data-testid="navbar-quote-btn"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-600 hover:text-sky-600 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-slate-100"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                link.isHash ? (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link); }}
                    className="block px-4 py-3 text-base font-medium text-slate-700 hover:text-sky-600 
                               hover:bg-sky-50 rounded-xl transition-colors"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-base font-medium text-slate-700 hover:text-sky-600 
                               hover:bg-sky-50 rounded-xl transition-colors"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <a 
                  href="tel:9132309208" 
                  className="flex items-center justify-center gap-2 py-3 text-slate-700 hover:text-sky-600"
                  data-testid="mobile-phone"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">(913) 230-9208</span>
                </a>
                <Button
                  onClick={() => { setIsMobileMenuOpen(false); onOpenQuote(); }}
                  className="w-full rounded-full bg-gradient-to-r from-pink-500 to-pink-600 
                             text-white py-6 shadow-lg"
                  data-testid="mobile-quote-btn"
                >
                  Get Free Quote
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
